import { FC, RefObject, useEffect } from 'react';
import { toast } from 'sonner';

interface IChatbotPreviewPanelProps {
  demoUrl: string;
  shouldRenderIframe: boolean;
  iframeLoading: boolean;
  chatHeight: number;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  onReset: () => void;
  onIframeSettled: () => void;
  /** Dev-only diagnostics; rendered solely when NODE_ENV === 'development'. */
  debug: {
    isReadyToRenderIframe: boolean;
    isFetching: boolean;
    hasData: boolean;
    hasToken: boolean;
    vendorId?: number | string;
  };
}

/**
 * Live preview of the chatbot widget embedded via iframe. Extracted from
 * Customize so the form and its preview are independently readable. Render
 * gating (Strict Mode, bot-data-loaded, valid demoUrl) is computed by the
 * parent and passed in as shouldRenderIframe.
 */
const ChatbotPreviewPanel: FC<IChatbotPreviewPanelProps> = ({
  demoUrl,
  shouldRenderIframe,
  iframeLoading,
  chatHeight,
  iframeRef,
  onReset,
  onIframeSettled,
  debug,
}) => {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'NAVIGATE_TO_PREVIEW' || !event.data.url) return;
      const target = String(event.data.url);
      toast.info('Auto-navigate (preview only)', {
        description: `On your live site, visitors would be redirected to ${target}. Redirects are disabled in the dashboard preview.`,
        duration: 6000,
      });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className='h-full bg-slate-50 flex flex-col items-center p-6 overflow-y-auto'>
      <div className='w-full max-w-[460px] flex justify-end mb-2'>
        <button
          type='button'
          onClick={onReset}
          disabled={!shouldRenderIframe}
          className='px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg text-xs font-medium text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          title='Start a fresh chatbot session in the preview (clears stored conversation so CTA buttons re-appear).'
        >
          Reset Preview
        </button>
      </div>
      {shouldRenderIframe ? (
        <section className='chatbot w-full max-w-[460px] relative'>
          {iframeLoading && (
            <div className='absolute inset-0 flex items-center justify-center bg-slate-100 rounded-lg z-10'>
              <div className='text-sm text-slate-500'>Loading preview...</div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            id='bot-preview-iframe'
            key={demoUrl}
            src={demoUrl}
            onLoad={onIframeSettled}
            onError={onIframeSettled}
            style={{
              border: '2px solid #e2e8f0',
              width: '100%',
              minWidth: '0',
              height: chatHeight + 'px',
              borderRadius: '0.75rem',
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.1) 0px 8px 10px -6px',
              display: 'block',
              backgroundColor: 'white',
            }}
            allow='microphone; camera; geolocation'
            referrerPolicy='no-referrer-when-downgrade'
            title='Chatbot Preview'
          ></iframe>
        </section>
      ) : (
        <div className='text-sm text-slate-500 text-center w-full pt-8'>
          <p>Loading preview...</p>
          {process.env.NODE_ENV === 'development' && (
            <div className='text-xs mt-2 text-slate-400 space-y-1'>
              <p>
                {!debug.isReadyToRenderIframe
                  ? 'Waiting for Strict Mode cycle...'
                  : debug.isFetching
                    ? 'Fetching bot configuration from API...'
                    : !debug.hasData
                      ? 'Waiting for bot data...'
                      : !demoUrl
                        ? 'Waiting for token/vendor config...'
                        : `URL: ${String(demoUrl).substring(0, 60)}...`}
              </p>
              <p>
                Token: {debug.hasToken ? 'present' : 'missing'}, VendorId: {debug.vendorId ?? 'missing'}
              </p>
              <p>
                Strict Mode: {debug.isReadyToRenderIframe ? '✓' : '⏳'}, Bot Data:{' '}
                {debug.isFetching ? '⏳' : debug.hasData ? '✓' : '✗'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatbotPreviewPanel;
